import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cssMap } from '@compiled/react';

import { type DocNode } from '@atlaskit/adf-schema';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	CHAT_STORE_STATUS,
	type StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { ShareUpToMessageButton } from '../../../common/ui/message-footer-buttons/share-response/ShareUpToMessageButton';
import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useConversation } from '../../../controllers/conversation/useConversation';
import { type WithInteractionMode } from '../interaction-mode';

import { ContentContainer } from './content-container';
import { FileListDisplay } from './content-container/file-list-display';
import { CreatePromptModalWrapper } from './create-prompt-modal-wrapper';
import { extractText } from './extract-text';
import { InlineEditPromptEditor } from './inline-edit/InlineEditPromptEditor';
import { isMessageEmpty } from './isMessageEmpty';
import { RedactedPromptContent } from './redacted-prompt/RedactedPromptContent';
import { RedactionEnforcementMessage } from './redacted-prompt/RedactionEnforcementMessage';

type StoreHumanViewCommonProps = {
	storeHuman: StoreHuman;
	respondedAgentId?: string;
};

type LiveStoreHumanViewProps = StoreHumanViewCommonProps & {
	onEditAndResend?: (newPrompt: DocNode | string) => void;
};

type ReadOnlyStoreHumanViewProps = StoreHumanViewCommonProps & {
	onEditAndResend?: never;
};

type LiveHumanMessageBindingsProps = LiveStoreHumanViewProps & {
	shouldRenderRedactionEnforcementMessage: boolean;
};

type StoreHumanViewProps = WithInteractionMode<
	LiveStoreHumanViewProps,
	ReadOnlyStoreHumanViewProps
>;

const styles = cssMap({
	fileUploadWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
});

type HumanMessagePresentationProps = StoreHumanViewCommonProps & {
	conversationId?: string;
	onMouseEnter?: () => void;
	onContentCopy?: () => void;
	shareSlot?: React.ReactNode;
	onCreatePrompt?: () => void;
	onEditClick?: () => void;
	renderedContentRef?: React.RefObject<HTMLDivElement>;
	shouldRenderRedactionEnforcementMessage: boolean;
};

const HumanMessagePresentation = ({
	storeHuman,
	respondedAgentId,
	conversationId,
	onMouseEnter,
	onContentCopy,
	shareSlot,
	onCreatePrompt,
	onEditClick,
	renderedContentRef,
	shouldRenderRedactionEnforcementMessage,
}: HumanMessagePresentationProps): React.JSX.Element | null => {
	const {
		content: { content, file_uploads },
		redactedPrompt,
	} = storeHuman;
	const {
		productKey,
		siteId,
		profileData: { aaid },
	} = useGlobalConfig();
	const { customHandlers } = useUIConfig();
	const copyContent = useCallback(() => {
		const plainText =
			typeof content === 'object' && content.type === 'doc'
				? extractText(content)
				: (content?.toString() ?? '');
		void navigator.clipboard?.writeText(plainText);
	}, [content]);

	if (isMessageEmpty(storeHuman)) {
		return null;
	}

	if (redactedPrompt && fg('rovo-guard-ui-redaction-and-blocking')) {
		return (
			<>
				<ContentContainer>
					<RedactedPromptContent redactedPrompt={redactedPrompt} />
				</ContentContainer>
				{shouldRenderRedactionEnforcementMessage && <RedactionEnforcementMessage />}
			</>
		);
	}

	return (
		<>
			<ContentContainer
				onMouseEnter={onMouseEnter}
				onContentCopy={onContentCopy ?? copyContent}
				shareSlot={shareSlot}
				onCreatePrompt={onCreatePrompt}
				onEditClick={onEditClick}
			>
				<Box ref={renderedContentRef}>
					<ContentRenderer
						content={content}
						productKey={productKey}
						siteId={siteId}
						aaid={aaid}
						theme="inverse"
						conversationId={conversationId}
						onDeepLinkClick={customHandlers?.handleDeepLinkClick}
						respondedAgentId={respondedAgentId}
					/>
				</Box>
			</ContentContainer>
			{!!file_uploads?.length && fg('rovo_enable_file_upload') && (
				<Box paddingBlockStart="space.400" xcss={styles.fileUploadWrapper}>
					<FileListDisplay files={file_uploads} />
				</Box>
			)}
		</>
	);
};

const LiveHumanMessageBindings = (
	props: LiveHumanMessageBindingsProps,
): React.JSX.Element | null => {
	const { storeHuman, onEditAndResend, respondedAgentId, shouldRenderRedactionEnforcementMessage } =
		props;
	const renderedContentRef = useRef<HTMLDivElement>(null);
	const [isCreatePromptModalOpen, setIsCreatePromptModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const { content } = storeHuman.content;

	const {
		productKey,
		siteId,
		profileData: { aaid },
	} = useGlobalConfig();
	const { conversation } = useConversation();
	const conversationId = conversation?.id ?? '';

	const { sendAnalyticsEvent } = useAnalytics();

	// Edit pencil is disabled while the assistant is mid-stream; it re-enables
	// when status returns to idle (stream completed, user cancelled, or error).
	const isEditDisabled =
		conversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
		conversation?.status === CHAT_STORE_STATUS.typingResponse;

	const getPlainTextClipboardContent = useCallback(() => {
		if (typeof content === 'object' && content.type === 'doc') {
			return extractText(content);
		}
		return content?.toString() ?? '';
	}, [content]);

	const handleCopyButtonAppear = useCallback(() => {
		sendAnalyticsEvent({
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'copyUserMessageButton',
			attributes: {
				productKey,
				siteId,
				userId: aaid,
				messageId: storeHuman.content.id,
			},
		});
	}, [productKey, siteId, aaid, storeHuman.content.id, sendAnalyticsEvent]);

	const handleCopyClick = useCallback(async () => {
		let node = null;
		if (renderedContentRef.current) {
			node = renderedContentRef.current;
		}

		const plainTextClipboardContent = getPlainTextClipboardContent();
		const messageLength = plainTextClipboardContent.length;

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'copyUserMessageButton',
			attributes: {
				productKey,
				siteId,
				userId: aaid,
				messageId: storeHuman.content.id,
				messageLength,
			},
		});

		try {
			if (node) {
				let cleanedHTML = node.innerHTML.replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '');
				const htmlBlob = new Blob([cleanedHTML], { type: 'text/html' });
				const textBlob = new Blob([plainTextClipboardContent], { type: 'text/plain' });
				await navigator.clipboard.write([
					new ClipboardItem({
						'text/html': htmlBlob,
						'text/plain': textBlob,
					}),
				]);
			} else {
				await navigator.clipboard.writeText(plainTextClipboardContent);
			}
		} catch {
			await navigator.clipboard.writeText(plainTextClipboardContent);
		}
	}, [
		getPlainTextClipboardContent,
		productKey,
		siteId,
		aaid,
		storeHuman.content.id,
		sendAnalyticsEvent,
	]);

	const messageText = useMemo(() => getPlainTextClipboardContent(), [getPlainTextClipboardContent]);

	const handleEditClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'editUserMessageButton',
			attributes: {
				productKey,
				siteId,
				userId: aaid,
				conversationId,
				messageId: storeHuman.content.id,
			},
		});
		setIsEditing(true);
	}, [productKey, siteId, aaid, conversationId, storeHuman.content.id, sendAnalyticsEvent]);

	const handleEditCancel = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'cancelEditUserMessageButton',
			attributes: {
				productKey,
				siteId,
				userId: aaid,
				conversationId,
				messageId: storeHuman.content.id,
			},
		});
		setIsEditing(false);
	}, [productKey, siteId, aaid, conversationId, storeHuman.content.id, sendAnalyticsEvent]);

	// If the bubble loses its edit affordance while editing (e.g. user sends a new
	// message from the main composer, so this is no longer the last human), flip
	// `isEditing` back to false so local state matches the now-unmounted editor.
	// This is a programmatic dismissal (not a user click), so we bypass
	// `handleEditCancel` to avoid emitting a misleading `clicked` analytics event.
	useEffect(() => {
		if (isEditing && !onEditAndResend) {
			setIsEditing(false);
		}
	}, [isEditing, onEditAndResend]);

	const handleEditUpdate = useCallback(
		(newPrompt: string | DocNode) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'saveEditUserMessageButton',
				attributes: {
					productKey,
					siteId,
					userId: aaid,
					conversationId,
					messageId: storeHuman.content.id,
				},
			});
			setIsEditing(false);
			onEditAndResend?.(newPrompt);
		},
		[
			productKey,
			siteId,
			aaid,
			conversationId,
			storeHuman.content.id,
			sendAnalyticsEvent,
			onEditAndResend,
		],
	);

	if (isEditing && onEditAndResend) {
		return (
			<InlineEditPromptEditor
				conversationId={conversationId}
				initialContent={content as DocNode | string}
				onCancel={handleEditCancel}
				onUpdate={handleEditUpdate}
			/>
		);
	}

	return (
		<>
			<HumanMessagePresentation
				storeHuman={storeHuman}
				respondedAgentId={respondedAgentId}
				conversationId={conversation?.id}
				onMouseEnter={handleCopyButtonAppear}
				onContentCopy={handleCopyClick}
				shareSlot={<ShareUpToMessageButton messageId={storeHuman.content.id} />}
				onCreatePrompt={
					FeatureGates.getExperimentValue('rovo_chat_prompt_library_v2_exp', 'isEnabled', false)
						? () => setIsCreatePromptModalOpen(true)
						: undefined
				}
				onEditClick={onEditAndResend && !isEditDisabled ? handleEditClick : undefined}
				renderedContentRef={renderedContentRef}
				shouldRenderRedactionEnforcementMessage={shouldRenderRedactionEnforcementMessage}
			/>
			{isCreatePromptModalOpen && (
				<CreatePromptModalWrapper
					initialPromptText={messageText}
					onClose={() => setIsCreatePromptModalOpen(false)}
				/>
			)}
		</>
	);
};

export const StoreHumanView: React.MemoExoticComponent<
	(props: StoreHumanViewProps) => React.JSX.Element | null
> = memo((props: StoreHumanViewProps): React.JSX.Element | null => {
	if (!props.interactionMode) {
		return (
			<LiveHumanMessageBindings
				storeHuman={props.storeHuman}
				onEditAndResend={props.onEditAndResend}
				respondedAgentId={props.respondedAgentId}
				shouldRenderRedactionEnforcementMessage={false}
			/>
		);
	}

	const { interactionMode } = props;
	switch (interactionMode) {
		case 'live':
			return (
				<LiveHumanMessageBindings
					storeHuman={props.storeHuman}
					onEditAndResend={props.onEditAndResend}
					respondedAgentId={props.respondedAgentId}
					shouldRenderRedactionEnforcementMessage
				/>
			);
		case 'read-only':
			return (
				<HumanMessagePresentation
					storeHuman={props.storeHuman}
					respondedAgentId={props.respondedAgentId}
					shouldRenderRedactionEnforcementMessage
				/>
			);
		default: {
			const exhaustiveInteractionMode: never = interactionMode;
			return exhaustiveInteractionMode;
		}
	}
});
